import React from 'react';
import PortofolioPosition from '../../../../api/models/portfolioPosition';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { selectIsCategoryCollapsed } from './collapsible-composite-slice';
import ValueDisplayFlipCard from './value-display-flip-card';
import DeviationGauge from '../../deviation-gauge';
import { setSelectedPosition } from './collapsible-position-slice';
import { instanceToPlain } from 'class-transformer';
import { positionDescription } from '../../../../app/utils';
import { PiLightningDuotone } from 'react-icons/pi';

interface AssetAllocationCategoryProps {
  portfolioPosition: PortofolioPosition
  deviationLimit: number
}

/**
 * A button that flips on click and alternates displayed portfolio values between MONEY (R$) and PERCENTAGE (%).
 */
const CollapsiblePosition: React.FC<AssetAllocationCategoryProps> = (props: AssetAllocationCategoryProps) => {

  // Get the 'dispatch' method from the store
  const dispatch = useAppDispatch();

  const parentCollapsed = useAppSelector(selectIsCategoryCollapsed(props.portfolioPosition.parentCompositeId || ''));
  const hasDynamicWeight = props.portfolioPosition?.dynamicWeight !== undefined && props.portfolioPosition?.dynamicWeight !== null && props.portfolioPosition?.dynamicWeight.weightPriceRanges.length > 0;

  const selectPositionHandler = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    dispatch(setSelectedPosition(Object(instanceToPlain(props.portfolioPosition))));
  }

  const unselectPositionHandler = (e: React.FocusEvent<HTMLTableRowElement>) => {
    dispatch(setSelectedPosition(undefined));
  }

  return (
    <tr onBlur={unselectPositionHandler} onClick={selectPositionHandler} tabIndex={0} hidden={parentCollapsed} className='group cursor-pointer font-normal border-b border-blue-300/40 bg-white/20 dark:bg-white/20 focus-within:bg-orange-300/40 hover:bg-orange-300/40'>
      <td colSpan={3} className='px-1 lg:px-6 pt-2 pb-1 lg:py-3 text-right'>
        <div className='flex flex-row justify-end items-center space-x-5'>
          <div className='hidden lg:flex flex-col grow text-start lg:text-end lg:space-y-1 text-xs'>
            <label className='cursor-pointer text-nowrap'>{props.portfolioPosition.quantity.toLocaleString('pt-BR')} @ R${props.portfolioPosition.price.toLocaleString('pt-BR')}</label>
            <label className='cursor-pointer text-nowrap lg:ml-1'>({props.portfolioPosition.priceDate.toLocaleString([], { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(', 00:00', '')})</label>
          </div>
          <label className='cursor-pointer font-semibold'>
            {
              positionDescription(props.portfolioPosition)
            }
          </label>
        </div>
      </td>
      <td className='text-center pt-2 pb-1 lg:py-3'>
        <ValueDisplayFlipCard
          moneyValue={props.portfolioPosition.idealNotional()}
          percentageValue={props.portfolioPosition.idealPercentage() * 100}
          className='cursor-pointer text-center w-full' />
      </td>
      <td className='max-lg:hidden flex px-1 lg:px-6 pt-2 pb-1 lg:py-3 justify-center'>
        <DeviationGauge
          deviation={props.portfolioPosition.deviation()}
          deviationLimit={props.deviationLimit}
          className='w-14'
          valueClassName='cursor-pointer text-center w-14 text-xs font-semibold bg-transparent'
        />
      </td>
      <td className='max-lg:hidden text-center pt-2 pb-1 lg:py-3'>
        <ValueDisplayFlipCard
          moneyValue={props.portfolioPosition.notional()}
          percentageValue={props.portfolioPosition.currentPercentage() * 100}
          className='cursor-pointer text-center w-full' />
      </td>
      <td className='text-center px-1 lg:px-6 pt-2 pb-1 lg:py-3'>
        <div className='flex flex-row items-center justify-center' title='Peso Dinâmico'>
          <label className='cursor-pointer'>{props.portfolioPosition.weight}</label>
          {
            hasDynamicWeight &&
            <PiLightningDuotone size={20} className='ml-1 text-yellow-300' />
          }
        </div>
      </td>
    </tr >
  )
}

export default CollapsiblePosition;